import { useUser, useAuth as useClerkAuth, useClerk } from '@clerk/clerk-react'
import { useConvexAuth, useQuery } from 'convex/react'
import { api } from '@convex/_generated/api'

/**
 * Admin profile data from Clerk user metadata or Convex.
 */
export interface AdminProfile {
  id: string
  email: string
  full_name: string | null
  role: 'admin' | 'super_admin'
}

export interface AuthState {
  /** The Clerk user object */
  user: {
    id: string
    email: string | undefined
    fullName: string | null
    imageUrl: string
  } | null
  /** Whether authentication state is still loading */
  isLoading: boolean
  /** Whether the user is signed in */
  isSignedIn: boolean
  /** Admin profile verified by the backend */
  adminProfile: AdminProfile | null
  /** Whether admin verification is still loading */
  isAdminLoading: boolean
  /** Whether user is signed in but not an admin */
  isUnauthorized: boolean
}

export interface UseAuthReturn extends AuthState {
  /** Sign out the current user */
  signOut: () => Promise<void>
  /** Get the current session token for API calls */
  getToken: () => Promise<string | null>
}

/**
 * Hook for managing Clerk authentication state.
 * Provides user info, sign out, and token retrieval for API calls.
 *
 * Signing in alone does not grant admin access.
 */
export function useAuth(): UseAuthReturn {
  const { user, isLoaded: userLoaded, isSignedIn } = useUser()
  const { getToken } = useClerkAuth()
  const clerk = useClerk()
  const convexAuth = useConvexAuth()
  const profile = useQuery(api.users.getMe, convexAuth.isAuthenticated ? {} : 'skip')
  const isAdminLoading = !!isSignedIn && (convexAuth.isLoading || (convexAuth.isAuthenticated && profile === undefined))

  // Build user object from Clerk user
  const userObject = user
    ? {
        id: user.id,
        email: user.primaryEmailAddress?.emailAddress,
        fullName: user.fullName,
        imageUrl: user.imageUrl,
      }
    : null

  const adminProfile: AdminProfile | null = profile
    ? {
        id: profile._id,
        email: profile.email,
        full_name: profile.full_name ?? null,
        role: profile.role,
      }
    : null

  // Sign out function
  async function signOut(): Promise<void> {
    await clerk.signOut()
  }

  // Get token for API calls
  // This will be used by api.ts for authenticated requests
  async function getSessionToken(): Promise<string | null> {
    try {
      // Get a Clerk session token for API authentication
      const token = await getToken()
      return token
    } catch (error) {
      console.error('Failed to get session token:', error)
      return null
    }
  }

  return {
    user: userObject,
    isLoading: !userLoaded || isAdminLoading,
    isSignedIn: isSignedIn ?? false,
    adminProfile,
    isAdminLoading,
    isUnauthorized: !!isSignedIn && !isAdminLoading && !profile,
    signOut,
    getToken: getSessionToken,
  }
}
